import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { formatDate } from '../lib/utils';
import { AlertCircle, Info, AlertTriangle, XCircle, Clock } from 'lucide-react';

const getLevelIcon = (level) => {
  switch (level) {
    case 'ERROR':
    case 'CRITICAL':
      return <XCircle className="h-4 w-4" />;
    case 'WARNING':
      return <AlertTriangle className="h-4 w-4" />;
    case 'INFO':
      return <Info className="h-4 w-4" />;
    default:
      return <AlertCircle className="h-4 w-4" />;
  }
};

const getLevelColor = (level) => {
  switch (level) {
    case 'ERROR':
    case 'CRITICAL':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'WARNING':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'INFO':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const getLevelBgColor = (level) => {
  switch (level) {
    case 'ERROR':
    case 'CRITICAL':
      return 'bg-red-50 border-red-200';
    case 'WARNING':
      return 'bg-orange-50 border-orange-200';
    case 'INFO':
      return 'bg-blue-50 border-blue-200';
    default:
      return 'bg-gray-50 border-gray-200';
  }
};

export function LogsTable({ logs }) {
  // Render Mobile Card View
  const renderMobileCard = (log) => {
    return (
      <Card key={log.id} className={`mb-3 ${getLevelBgColor(log.level)}`}>
        <CardContent className="p-4 space-y-3">
          {/* Header - Level & Time */}
          <div className="flex items-start justify-between gap-3">
            <Badge className={`${getLevelColor(log.level)} border flex items-center gap-1.5 px-2.5 py-1`}>
              {getLevelIcon(log.level)}
              <span className="font-medium">{log.level}</span>
            </Badge>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span>{formatDate(log.timestamp)}</span>
            </div>
          </div>

          {/* Message */}
          <div className="text-sm leading-relaxed break-words">
            {log.message}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      {/* Desktop Table View - Hidden on mobile */}
      <div className="hidden md:block rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Waktu</TableHead>
              <TableHead className="w-[120px]">Tingkat</TableHead>
              <TableHead>Pesan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                  Tidak ada log aktivitas
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="text-sm">
                    {formatDate(log.timestamp)}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${getLevelColor(log.level)} border flex items-center gap-1.5 w-fit`}>
                      {getLevelIcon(log.level)}
                      <span>{log.level}</span>
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-2xl">
                    <span className="break-words">{log.message}</span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View - Visible only on mobile */}
      <div className="md:hidden space-y-3">
        {logs.length === 0 ? (
          <Card>
            <CardContent className="text-center text-muted-foreground py-8">
              Tidak ada log aktivitas
            </CardContent>
          </Card>
        ) : (
          logs.map(log => renderMobileCard(log))
        )}
      </div>
    </>
  );
}